import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiChevronDown } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import styles from './AppLayout.module.css';

const FinancialYearSelector = () => {
  const {
    activeDerasarId,
    financialYearsLoaded,
    visibleFinancialYears,
    activeFinancialYearId,
    setActiveFinancialYearId,
    isDerasarAdmin,
    user,
  } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const shouldShow = useMemo(
    () => !!activeDerasarId,
    [activeDerasarId],
  );

  useEffect(() => {
    const handleClick = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!shouldShow || !financialYearsLoaded) return null;

  const isAdminOrSuperAdmin = isDerasarAdmin || user?.role === 'super_admin' || user?.role === 'platform_admin';

  if (visibleFinancialYears.length === 0) {
    if (!isAdminOrSuperAdmin) return null;
    return (
      <button
        type="button"
        className={styles.financialYearPrompt}
        onClick={() => navigate('/accounting/financial-years')}
      >
        <FiCalendar />
        First create a financial year
      </button>
    );
  }

  const activeFinancialYear = visibleFinancialYears.find(
    (financialYear) => String(financialYear.id) === String(activeFinancialYearId),
  );
  const buttonLabel = activeFinancialYear?.yearLabel || 'Select year';

  return (
    <div className={styles.financialYearDropdownWrap} ref={ref}>
      <button
        type="button"
        className={styles.financialYearWrap}
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className={styles.financialYearIcon}>
          <FiCalendar />
        </div>
        <span className={styles.financialYearValue}>{buttonLabel}</span>
        <div className={`${styles.financialYearChevron} ${open ? styles.financialYearChevronOpen : ''}`}>
          <FiChevronDown />
        </div>
      </button>

      {open && (
        <div className={styles.financialYearMenu} role="listbox" aria-label="Select financial year">
          {isAdminOrSuperAdmin && (
            <div
              className={styles.financialYearOption}
              onClick={() => {
                setOpen(false);
                navigate('/accounting/financial-years');
              }}
              role="option"
              aria-selected={!activeFinancialYearId}
            >
              Select year
            </div>
          )}
          {visibleFinancialYears.map((financialYear) => {
            const isActive = String(financialYear.id) === String(activeFinancialYearId);
            return (
              <div
                key={financialYear.id}
                className={`${styles.financialYearOption} ${isActive ? styles.financialYearOptionActive : ''}`}
                onClick={() => {
                  setActiveFinancialYearId(financialYear.id);
                  setOpen(false);
                }}
                role="option"
                aria-selected={isActive}
              >
                {financialYear.yearLabel}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FinancialYearSelector;
